import { homerunnerApiBaseUrl } from '../../hr'
import { okOrThrow } from '../../common/http'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { SortSettings, VisitorFilter } from './config'
import { resolveDefaultSort } from './sort'
import type { UnitGroup } from '../../common/property-card/unit-group'

export type ExploreProperty = {
  id: string
  slug: string | null
  propertyType: string
  title: string
  price: number
  // ISO 4217 code for every price on this row (#1489), sent by Central since
  // central#3441 under the same key /feed/property-pins uses. Optional on
  // purpose: a Central that predates it omits the key, an SSR page cached before
  // it keeps such rows for up to 24h, and a blank column arrives as null. All
  // three read as USD, which is what the card printed before it had a choice.
  currency?: string | null
  images: string[]
  altText: string
  tag: string
  // Taxonomy term lists for the grid-left/right badges (central #2615). Optional/additive.
  groups?: { id: number; name: string }[]
  locations?: { id: number; name: string }[]
  tags?: { id: number; name: string }[]
  // Per-property amenity terms (central #2849 / dashboard #822), same {id,name} shape.
  amenities?: { id: number; name: string }[]
  // Id-bearing property type / city relations (central#3137, Label Designer v2
  // #1364). SINGULAR (not arrays, unlike groups/tags/locations/amenities above)
  // — one property has one type and one city. Null when the property has no
  // relation; absent on payloads that predate central#3137. `propertyType`
  // (name-only) and `location.label` below stay as the legacy display fields.
  type?: { id: number; name: string } | null
  city?: { id: number; name: string } | null
  // Card subtitle sources (central #2615 / WP subtitle_source). Keyed by WP source name.
  subtitle?: {
    post_title?: string | null
    post_excerpt?: string | null
    name?: string | null
    city?: string | null
    state?: string | null
    country?: string | null
  }
  location: {
    label: string
    lat: number
    lng: number
  }
  features: {
    guests: number
    baths: number
    beds: number
  }
  rating?: { average: number; count: number } | null
  // Calendar-derived price summary (#2565). lowest/average/highest are the no-dates
  // forward-window materialized prices; datesPrice is the per-stay total/avg over the
  // requested checkin/checkout window (present only when dates are queried).
  lowestPrice?: number | null
  averagePrice?: number | null
  highestPrice?: number | null
  datesPrice?: {
    total: number | null
    average: number | null
    lowest: number | null
    nights: number
  } | null
  // Calendar freshness (central#3089). bookableNights = forward nights with
  // is_available=1 at the last successful calendar sync, counted over ALL forward
  // rows (0 = rows exist, none available; null = no forward rows at all);
  // calendarSyncedAt = that sync's timestamp; calendarStale = Central's verdict that
  // the stamp is missing or older than the platform's own sync interval allows.
  // All three are absent on payloads that predate the fields.
  // calendarSyncedAt is carried for the payload shape (a "last synced" display, the
  // dashboard fleet view); the card gate reads only bookableNights and calendarStale.
  bookableNights?: number | null
  calendarSyncedAt?: string | null
  calendarStale?: boolean
  // Unit Grouping (#1070, central#3210). Set only on a group's representative
  // row when the feed has `additional_info.unit_grouping.enabled`; every
  // other row (sibling rows, and every row on an unconfigured feed) carries
  // null. Absent entirely on payloads that predate central#3210.
  unit_group?: UnitGroup | null
}

export type ExplorePagination = {
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
  hasNextPage: boolean
  hasPrevPage: boolean
  // Unit Grouping (#1070, central#3210): non-null only when the feed has
  // grouping enabled. Absent on payloads that predate central#3210.
  unitGrouping?: { candidates: number; collapsed: number } | null
}

export type TaxonomyOption = {
  id: number
  name: string
}

export type ExplorerMeta = {
  placeTypes: string[]
  priceRange: {
    min: number
    max: number
  }
  // Fleet per-date minimum-stay aggregate (central#3208, dashboard#1071 —
  // toolkit `explorer-dynamic-min-stay` parity): `MIN(minimum_stay)` across
  // the feed's available member properties arriving on that date, for UTC
  // yesterday through +365 days. Dates with no available property or no
  // positive minimum are OMITTED (no constraint), never present as 0 — see
  // DateFilter.tsx's `effectiveMinStay`, which is the only reader. Optional
  // for back-compat with cached payloads from before this field existed.
  minStayByDate?: Record<string, number>
  amenities: string[]
  location: {
    cities: {
      id: number
      name: string
      title: string
      description: string
      image: string
    }[]
    // States/countries are free-text property columns, returned as string-keyed
    // options (id === value) so the Where field can filter by the raw value (#511).
    states?: { id: string; name: string }[]
    countries?: { id: string; name: string }[]
  }
  // Id-bearing taxonomy options (central #2562). Optional for back-compat with
  // older cached payloads that predate the `taxonomies` block.
  taxonomies?: {
    types: TaxonomyOption[]
    amenities: TaxonomyOption[]
    locations: TaxonomyOption[]
    groups: TaxonomyOption[]
    tags: TaxonomyOption[]
    cities: TaxonomyOption[]
  }
}

/**
 * Curate a filter option list (#511): keep only the admin-selected ids, in the
 * admin's chosen order. An empty/undefined curation passes the full list through
 * unchanged (default = show all in the source order).
 */
export function curateOptions<T extends { id: string | number }>(all: T[], curated?: string[]): T[] {
  if (!curated || curated.length === 0) return all
  const order = new Map(curated.map((id, i) => [id, i]))
  return all
    .filter(o => order.has(String(o.id)))
    .sort((a, b) => (order.get(String(a.id)) ?? 0) - (order.get(String(b.id)) ?? 0))
}

export async function fetchProperties(
  feedId: number,
  visitorFilters: Partial<VisitorFilter>,
  page: number = 1,
  limit: number = 12,
  platformUuid?: string,
) {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/${feedId}/feed/properties`)
  url.searchParams.append('limit', String(limit))
  url.searchParams.append('page', String(page))

  if (platformUuid) {
    url.searchParams.append('platform', platformUuid)
  }

  Object.entries(visitorFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, String(value))
    }
  })

  const res = await fetch(url.toString())
  await okOrThrow(res, 'Failed to fetch properties')
  const data = await res.json() as {
    status: 'ok' | 'error'
    result: ExploreProperty[]
    pagination: ExplorePagination
  }

  // exclude_property is forwarded to the public API above and filtered
  // server-side before pagination (central #2563), so no client fallback.
  return data
}

export type PriceField = 'base' | 'lowest' | 'average'

// Map a card price-display mode to the Central price column used for the price filter,
// slider bounds, and price sort (WP: the configured mode drives which rent column these
// use). total/per_night/none are per-stay/display-only, so they fall back to the base column.
export function priceFieldForMode(mode: string | undefined): PriceField {
  if (mode === 'lowest') return 'lowest'
  if (mode === 'average') return 'average'
  return 'base'
}

// --- Shared query keys ------------------------------------------------------------
// The SSR prefetch (ssr.ts) dehydrates entries under these exact keys and every client
// hook below reads through them. Build keys ONLY via these helpers, and route every
// properties-filter object through `withBrowsePriceField`: a consumer that hand-rolls
// its filter shape forks the key, misses the dehydrated cache, and silently refetches
// client-side — that regression shipped once already (#508 added price_field to the
// grid but not to ssr.ts, so cached SSR listings contained skeletons).
//
// The invariant behind both halves of #1093: THE KEY MUST CARRY EVERY ARGUMENT THE
// queryFn CLOSES OVER. `fetchProperties(feedId, visitorFilters, page, limit, platformUuid)`
// — `page` is the pageParam, and the other four all appear below. `limit` used to be
// closed over but absent from the key, so two explorer instances with different page
// sizes shared one cache entry and whichever mounted first won (the QueryClient is a
// page-wide window singleton with refetchOnMount: false).

export function explorePropertiesKey(
  feedId: number,
  platformUuid: string | undefined,
  perPage: number,
  visitorFilters: Partial<VisitorFilter>,
) {
  return ['explore-properties', feedId, platformUuid, perPage, visitorFilters] as const
}

export function exploreMetaKey(
  feedId: number,
  platformUuid: string | undefined,
  priceField?: PriceField,
) {
  return ['explore-meta', feedId, platformUuid, priceField] as const
}

/** Attach the price column matching the browse (no-dates) price mode (#441). */
/**
 * A hidden room stepper must be fully inert (#852): URL params or visitor
 * defaults could otherwise keep an invisible bedrooms/bathrooms filter applied
 * with no UI to see or clear it. Shared by every property fetch (via
 * withBrowsePriceField) AND the redirect-on-submit URL serialization.
 */
export function stripHiddenRoomFilters(
  filters: Partial<VisitorFilter>,
  options: { showBedroomsFilter?: boolean; showBathroomsFilter?: boolean; showPetsFilter?: boolean },
): Partial<VisitorFilter> {
  const out = { ...filters }
  if (options.showBedroomsFilter === false) {
    delete out.bedrooms
    delete out.bedrooms_eq
  }
  if (options.showBathroomsFilter === false) {
    delete out.bathrooms
  }
  // #932 (same #558 intent as the row gate): a URL-seeded pets flag must not
  // act as an invisible active filter when the Pets row is hidden — no UI
  // exists to see or clear it.
  if (options.showPetsFilter === false) {
    delete out.pets
  }
  return out
}

/**
 * THE browse-query funnel. Takes the visitor's filter state and attaches the two
 * params that are DERIVED from admin config rather than chosen by the visitor:
 *
 * - `price_field` — the price column the filter/sort/bounds cut on (#441).
 * - `sort_by` — the configured default sort, unless the visitor has picked one
 *   (#1093). Seeded here rather than in `ExplorerProvider`'s initial state on
 *   purpose: state would be stamped into the page URL by `syncFiltersToUrl`
 *   (`sort_by` is a urlFilterKey) and would ride `useSearchSubmit`'s redirect
 *   into the listings page, overriding THAT page's own configured default.
 *   As a derived param it stays invisible to both, and `RESET_FILTERS` re-derives
 *   it for free.
 *
 * Every properties fetch — client hooks and the SSR prefetch alike — goes through
 * here, which is what keeps the SSR and hydration query keys identical.
 */
export function withBrowsePriceField(
  filters: Partial<VisitorFilter>,
  options: {
    priceDisplay: { noDates: string }
    sort: SortSettings
    showBedroomsFilter?: boolean
    showBathroomsFilter?: boolean
    showPetsFilter?: boolean
  },
): Partial<VisitorFilter> {
  return {
    ...stripHiddenRoomFilters(filters, options),
    price_field: priceFieldForMode(options.priceDisplay.noDates),
    sort_by: filters.sort_by ?? resolveDefaultSort(options.sort),
  }
}

export async function fetchExplorerMeta(feedId: number, platformUuid?: string, priceField?: PriceField) {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/${feedId}/feed/explorer-meta`)
  if (platformUuid) {
    url.searchParams.append('platform', platformUuid)
  }
  if (priceField) {
    url.searchParams.append('price_field', priceField)
  }
  const res = await fetch(url.toString())
  await okOrThrow(res, 'Failed to fetch explorer meta')
  const data = await res.json()

  return data as {
    status: 'ok' | 'error'
    result: ExplorerMeta
  }
}

export function useFetchProperties(
  feedId: number,
  visitorFilters: Partial<VisitorFilter>,
  perPage: number = 12,
  platformUuid?: string,
) {
  return useInfiniteQuery({
    queryKey: explorePropertiesKey(feedId, platformUuid, perPage, visitorFilters),
    queryFn: ({ pageParam = 1 }) =>
      fetchProperties(feedId, visitorFilters, pageParam, perPage, platformUuid),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.pagination?.hasNextPage) {
        return lastPage.pagination.page + 1
      }
      return undefined
    },
  })
}

// --- All-pins mode (#855, toolkit map-all-pins port) -------------------------------

/** One skinny pin from the property-pins endpoint (central#3106). `price`/
 *  `currency` are the spec's optional v1 extras — the widget handles both shapes. */
export type PropertyPin = {
  id: string
  slug: string | null
  title: string
  lat: number
  lng: number
  price?: number | null
  currency?: string | null
}

export type PropertyPinsResult = {
  pins: PropertyPin[]
  total: number
  capped: boolean
}

/**
 * The pins mirror of the browse query (#855): the same visitor filters and the
 * same derived `price_field` (it cuts the price filter, so dropping it would
 * fork the filtered set), but — per the central#3106 contract and the decision
 * inherited from the toolkit endpoint —
 * - no `sort_by` (pins are unordered; skipping sort skips the orderby work), and
 * - no `map_bounds` (full filtered set with a server-side cap, never
 *   viewport-bounded, so search-as-move panning never shrinks the pin set).
 * Pagination params never enter: `fetchPropertyPins` simply sends none.
 */
export function withPinsFilters(
  filters: Partial<VisitorFilter>,
  options: {
    priceDisplay: { noDates: string }
    showBedroomsFilter?: boolean
    showBathroomsFilter?: boolean
  },
): Partial<VisitorFilter> {
  const out = {
    ...stripHiddenRoomFilters(filters, options),
    price_field: priceFieldForMode(options.priceDisplay.noDates),
  }
  delete out.sort_by
  delete out.map_bounds
  return out
}

export async function fetchPropertyPins(
  feedId: number,
  pinsFilters: Partial<VisitorFilter>,
  platformUuid?: string,
): Promise<PropertyPinsResult> {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/${feedId}/feed/property-pins`)
  if (platformUuid) {
    url.searchParams.append('platform', platformUuid)
  }
  Object.entries(pinsFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, String(value))
    }
  })

  const res = await fetch(url.toString())
  await okOrThrow(res, 'Failed to fetch property pins')
  const data = await res.json() as {
    status: 'ok' | 'error'
    result: { pins?: PropertyPin[]; total?: number; capped?: boolean }
  }

  // Coordinate hygiene at the boundary (toolkit rule): numbers may arrive as
  // strings; a pin without finite coordinates can never render and must not
  // reach the map layer.
  const pins = (data.result?.pins ?? []).filter((pin) => {
    pin.lat = parseFloat(String(pin.lat))
    pin.lng = parseFloat(String(pin.lng))
    return !!pin.id && isFinite(pin.lat) && isFinite(pin.lng)
  })
  return { pins, total: data.result?.total ?? pins.length, capped: data.result?.capped === true }
}

/**
 * Pins are an enhancement, never the critical path: no retries (a failing or
 * not-yet-shipped endpoint must not be hammered — central#3106 may lag this
 * widget), errors leave the map exactly as the paged markers rendered it.
 *
 * The dated-stampede rule rides in via `enabled` (the caller gates dated
 * queries on the card request's success — see LeafletMap): react-query then
 * covers the rest of the toolkit's fetch choreography for free (key-level
 * dedupe, stale-response discard, last-good retention while disabled).
 */
export function usePropertyPins(
  feedId: number,
  pinsFilters: Partial<VisitorFilter>,
  platformUuid: string | undefined,
  opts: { enabled: boolean },
) {
  return useQuery({
    queryKey: ['explore-property-pins', feedId, platformUuid, pinsFilters] as const,
    queryFn: () => fetchPropertyPins(feedId, pinsFilters, platformUuid),
    enabled: opts.enabled,
    retry: false,
    staleTime: 60_000,
    // Keep the previous filter's pins on screen while the mirror refetches —
    // the toolkit keeps last-good pins rather than blanking the map.
    placeholderData: (prev: PropertyPinsResult | undefined) => prev,
  })
}

export function useFetchExplorerMeta(feedId: number, platformUuid?: string, priceField?: PriceField) {
  return useQuery({
    queryKey: exploreMetaKey(feedId, platformUuid, priceField),
    queryFn: () => fetchExplorerMeta(feedId, platformUuid, priceField),
  })
}

// Recommendations fallback (#436 §2.9): a curated set of properties shown when the
// visitor's filters return nothing. Sources from featured and/or tag-scoped properties,
// deliberately NOT carrying the visitor's (zero-result) filters — and deliberately NOT
// routed through `withBrowsePriceField`, so it carries neither the browse price column
// nor the configured default sort. It is a fixed curated set, not a view of the browse
// query; giving it either would be a bug, not a fix.
export function useFetchRecommendations(
  feedId: number,
  opts: { featured?: boolean; tags?: string; limit: number; enabled: boolean },
  platformUuid?: string,
) {
  const filters: Partial<VisitorFilter> = {}
  if (opts.featured) filters.featured = true
  if (opts.tags) filters.tags = opts.tags
  return useQuery({
    queryKey: ['explore-recommendations', feedId, platformUuid, opts.featured, opts.tags, opts.limit],
    queryFn: () => fetchProperties(feedId, filters, 1, opts.limit, platformUuid),
    enabled: opts.enabled,
  })
}

// --- Quotes API ---

export type QuoteRequest = {
  property: string
  checkin: string
  checkout: string
  adults?: number
  guests?: number
  children?: number
  pets?: number
  coupon?: string
}

// Field names mirror the public-api quote payload, which is camelCase
// (`Quote::setRateplansAttribute` in central). Reading a snake_case name here
// yields `undefined` and renders `$NaN` rather than failing loudly — see #1041.
export type QuoteRateplan = {
  total: number
  totalTaxes: number
  discount: number
}

export type QuoteResponse = {
  id: string
  property_id: string
  checkin: string
  checkout: string
  nights: number
  guests: number
  adults: number
  children: number
  rateplans: QuoteRateplan[]
  coupons: unknown[]
  /** Raw stored shape; the explorer never reads it. `selected_addons` on the
   *  checkout Quote is the normalised one (central#3400, dashboard#1047). */
  addons: unknown[]
}

export async function fetchQuote(
  feedId: number,
  params: QuoteRequest,
) {
  const url = `${homerunnerApiBaseUrl()}/public-api/v1/quotes`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...params, feed_id: feedId }),
  })
  await okOrThrow(res, 'Failed to create quote')
  const data = await res.json()

  return data as {
    success: boolean
    status_code: number
    result: QuoteResponse
  }
}

export function useFetchQuote(feedId: number) {
  return useMutation({
    mutationFn: (params: QuoteRequest) => fetchQuote(feedId, params),
  })
}
